import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase.config';

/**
 * Lee los extractos reales que deja la Cloud Function programada.
 *
 * Va directo a Firestore y no a la API por HTTP: la red de los casinos y la
 * oficina de INPRO bloquea cloudfunctions.net, pero no firestore.googleapis.com.
 * Es el mismo camino que ya usa la búsqueda de autoexcluidos.
 *
 * Si algo falla —sin conexión, todavía no corrió el scraper, datos raros— el
 * hook devuelve null y la página se queda con lo que tenía. Nunca rompe.
 */

export const LOTERIA_JUJUY = 6;

const ORDEN_TURNOS = ['La Previa', 'La Primera', 'Matutina', 'Vespertina', 'Nocturna'];

function ordenar(a, b) {
  const ia = ORDEN_TURNOS.indexOf(a.turno);
  const ib = ORDEN_TURNOS.indexOf(b.turno);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return a.turnoId - b.turnoId;
}

/**
 * Devuelve { datos, cargando }.
 *
 * El `cargando` importa: sin él no se puede distinguir "todavía no llegó" de
 * "no hay nada", y la página termina dibujando algo en el primer render, con
 * los extractos todavía en camino. En un sitio de lotería ese hueco no se
 * rellena con números de ejemplo — se espera.
 */
export function useExtractos() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const puntero = await getDoc(doc(db, 'extractos_estado/ultimo'));
        if (!puntero.exists()) return;

        const fechaISO = puntero.data().fechaISO;
        if (!fechaISO) return;

        const snap = await getDoc(doc(db, `extractos/${fechaISO}`));
        if (!snap.exists() || !vivo) return;

        const d = snap.data();
        if (!Array.isArray(d.quiniela) || d.quiniela.length === 0) return;

        setDatos(d);
      } catch (e) {
        // Silencioso a propósito: la página funciona igual sin esto.
        console.warn('No se pudieron cargar los extractos:', e?.message || e);
      } finally {
        if (vivo) setCargando(false);
      }
    })();

    return () => { vivo = false; };
  }, []);

  return { datos, cargando };
}

/**
 * El día de una fecha puntual, para los links compartidos.
 *
 * El hook de arriba lee siempre el ÚLTIMO día publicado; esto lee uno
 * cualquiera. Cada día es un documento con la fecha ISO de id, así que un
 * link de hace una semana sigue abriendo lo que prometía.
 *
 * Devuelve null si esa fecha no está publicada: quien llama muestra lo que
 * haya y lo dice. Nunca rompe la página y nunca inventa un dato (N1).
 */
export async function extractosDeFecha(fechaISO) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(fechaISO || ''))) return null;
  try {
    const snap = await getDoc(doc(db, `extractos/${fechaISO}`));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (!Array.isArray(d.quiniela) || d.quiniela.length === 0) return null;
    return d;
  } catch (e) {
    console.warn('No se pudo cargar el extracto de', fechaISO, e?.message || e);
    return null;
  }
}

/** Extractos de Jujuy del día, ordenados por turno. */
export function quinielaJujuy(datos) {
  if (!datos) return [];
  return datos.quiniela
    .filter((e) => e.loteriaId === LOTERIA_JUJUY && e.numeros?.length)
    .sort(ordenar);
}

/**
 * Arma las tarjetas de resultados con los datos reales.
 * Devuelve null si no hay nada útil, para que el componente use su fallback.
 */
export function tarjetasResultados(datos) {
  const jujuy = quinielaJujuy(datos);
  if (!jujuy.length) return null;

  const tarjetas = jujuy.slice(0, 2).map((e) => ({
    nombre: e.turno || `Sorteo ${e.turnoId}`,
    detalle: `Nº ${e.sorteoNro} · ${datos.fecha}`,
    numeros: e.numeros.slice(0, 6),
  }));

  const pozo = (datos.pozo || [])[0];
  if (pozo?.numeros?.length) {
    tarjetas.push({
      nombre: 'Pozo',
      detalle: pozo.sorteoNro ? `Nº ${pozo.sorteoNro} · ${pozo.fecha || ''}`.trim() : 'Super Bola',
      numeros: pozo.numeros,
      oro: pozo.superBola ?? undefined,
    });
  }

  return tarjetas.length ? tarjetas : null;
}

/** Marquesina: turnos de Jujuy con su cabeza, más los pozos estimados. */
export function ticker(datos) {
  if (!datos) return null;

  const items = quinielaJujuy(datos).map(
    (e) => `${e.turno || 'Sorteo ' + e.turnoId} ${e.numeros[0]}`
  );

  const pozo = (datos.pozo || [])[0];
  if (pozo?.numeros?.length) items.push(`Pozo ${pozo.numeros.join('·')}`);

  (datos.pozosEstimados || []).forEach((p) => {
    if (!p.juego || !p.monto) return;
    const millones = Math.round(p.monto / 1e6);
    const texto = millones >= 1000
      ? `$${(millones / 1000).toFixed(1).replace('.0', '')}MM`
      : `$${millones}M`;
    items.push(`${p.juego} ${texto}`);
  });

  return items.length ? items : null;
}
